import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTransactions } from '../context/TransactionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  Pencil, 
  Trash2, 
  FileText, 
  Scale, 
  Download,
  Search,
  Filter,
  HelpCircle
} from 'lucide-react';
import RecetteModal from '../components/RecetteModal';
import DepenseModal from '../components/DepenseModal';
import { toast } from 'sonner';
import { exportPDFMois } from '../utils/pdfExport';
import { useSubscription } from '../context/SubscriptionContext';
import { useSettings } from '../context/SettingsContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const MOIS_CONFIG = {
  janvier: { nom: 'Janvier', num: 1 },
  fevrier: { nom: 'Février', num: 2 },
  mars: { nom: 'Mars', num: 3 },
  avril: { nom: 'Avril', num: 4 },
  mai: { nom: 'Mai', num: 5 },
  juin: { nom: 'Juin', num: 6 },
  juillet: { nom: 'Juillet', num: 7 },
  aout: { nom: 'Août', num: 8 },
  septembre: { nom: 'Septembre', num: 9 },
  octobre: { nom: 'Octobre', num: 10 },
  novembre: { nom: 'Novembre', num: 11 },
  decembre: { nom: 'Décembre', num: 12 }
};

const MODES_PAIEMENT = ['Tous', 'Chèque', 'Virement', 'Espèces', 'CB', 'PayPal'];

// Format date en français JJ/MM/AAAA
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const MonthPage = () => {
  const { mois } = useParams();
  const moisConfig = MOIS_CONFIG[mois];
  const { 
    recettes, 
    depenses, 
    selectedYear, 
    fetchRecettes, 
    fetchDepenses,
    deleteRecette,
    deleteDepense,
    loading 
  } = useTransactions();
  const { hasWatermark, trackExport, canExport } = useSubscription();
  const { formatMontant, formatMontantAvecSigne } = useSettings();

  const [recetteModalOpen, setRecetteModalOpen] = useState(false);
  const [depenseModalOpen, setDepenseModalOpen] = useState(false);
  const [editingRecette, setEditingRecette] = useState(null);
  const [editingDepense, setEditingDepense] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });
  
  // Filtres
  const [searchRecettes, setSearchRecettes] = useState('');
  const [searchDepenses, setSearchDepenses] = useState('');
  const [filterModeRecettes, setFilterModeRecettes] = useState('Tous');
  const [filterModeDepenses, setFilterModeDepenses] = useState('Tous');

  useEffect(() => {
    if (moisConfig) {
      fetchRecettes(selectedYear, moisConfig.num);
      fetchDepenses(selectedYear, moisConfig.num);
    }
  }, [mois, selectedYear, moisConfig, fetchRecettes, fetchDepenses]);

  // Filtrage et calculs avec useMemo
  const { 
    filteredRecettes, 
    filteredDepenses, 
    totalRecettes, 
    totalDepenses, 
    solde 
  } = useMemo(() => {
    // Filtrer les recettes
    let filtRec = [...recettes];
    if (searchRecettes) {
      const search = searchRecettes.toLowerCase();
      filtRec = filtRec.filter(r => 
        r.client.toLowerCase().includes(search) ||
        r.nature.toLowerCase().includes(search) ||
        r.reference.toLowerCase().includes(search)
      );
    }
    if (filterModeRecettes !== 'Tous') {
      filtRec = filtRec.filter(r => r.mode_encaissement === filterModeRecettes);
    }
    
    // Filtrer les dépenses
    let filtDep = [...depenses];
    if (searchDepenses) {
      const search = searchDepenses.toLowerCase();
      filtDep = filtDep.filter(d => 
        d.fournisseur.toLowerCase().includes(search) ||
        d.nature.toLowerCase().includes(search) ||
        d.reference.toLowerCase().includes(search)
      );
    }
    if (filterModeDepenses !== 'Tous') {
      filtDep = filtDep.filter(d => d.mode_paiement === filterModeDepenses);
    }
    
    // Tri par date (du plus ancien au plus récent)
    filtRec.sort((a, b) => new Date(a.date) - new Date(b.date));
    filtDep.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calcul des totaux (sur toutes les transactions, pas filtrées)
    const totRec = recettes.reduce((sum, r) => sum + r.montant, 0);
    const totDep = depenses.reduce((sum, d) => sum + d.montant, 0);
    
    return {
      filteredRecettes: filtRec,
      filteredDepenses: filtDep,
      totalRecettes: totRec,
      totalDepenses: totDep,
      solde: totRec - totDep
    };
  }, [recettes, depenses, searchRecettes, searchDepenses, filterModeRecettes, filterModeDepenses]);

  const handleEditRecette = (recette) => {
    setEditingRecette(recette);
    setRecetteModalOpen(true);
  };

  const handleEditDepense = (depense) => {
    setEditingDepense(depense);
    setDepenseModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (deleteDialog.type === 'recette') {
        await deleteRecette(deleteDialog.id);
        toast.success('✓ Transaction supprimée');
        fetchRecettes(selectedYear, moisConfig.num);
      } else {
        await deleteDepense(deleteDialog.id);
        toast.success('✓ Transaction supprimée');
        fetchDepenses(selectedYear, moisConfig.num);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteDialog({ open: false, type: null, id: null });
  };

  const handleRecetteSuccess = () => {
    setRecetteModalOpen(false);
    setEditingRecette(null);
    fetchRecettes(selectedYear, moisConfig.num);
    toast.success(editingRecette ? '✓ Transaction mise à jour' : '✓ Recette enregistrée');
  };

  const handleDepenseSuccess = () => {
    setDepenseModalOpen(false);
    setEditingDepense(null);
    fetchDepenses(selectedYear, moisConfig.num);
    toast.success(editingDepense ? '✓ Transaction mise à jour' : '✓ Dépense enregistrée');
  };

  const handleExportPDF = async () => {
    // Vérifier si les données sont vides
    if (recettes.length === 0 && depenses.length === 0) {
      toast.error('Aucune donnée à exporter. Veuillez d\'abord saisir des recettes ou dépenses pour ce mois.');
      return;
    }
    
    if (!canExport()) {
      toast.error('Limite d\'exports atteinte. Passez à Solo pour des exports illimités.');
      return;
    }
    
    try {
      const fileName = exportPDFMois(moisConfig.nom, selectedYear, recettes, depenses, hasWatermark());
      await trackExport(); // Incrémenter le compteur
      toast.success(`✓ PDF exporté : ${fileName}`);
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  // Déterminer les couleurs selon le solde
  const getSoldeColor = () => {
    if (solde > 0) return 'text-emerald-600';
    if (solde < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  const getSoldeBgColor = () => {
    if (solde > 0) return 'bg-emerald-50 border-emerald-200';
    if (solde < 0) return 'bg-red-50 border-red-200';
    return 'bg-slate-50 border-slate-200';
  };

  if (!moisConfig) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Mois non trouvé</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="month-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-slate-900 tracking-tight">
              {moisConfig.nom} {selectedYear}
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos recettes et dépenses du mois
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleExportPDF}
              variant="outline"
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="export-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter PDF
            </Button>
            <Button 
              onClick={() => setRecetteModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-recette-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle recette
            </Button>
            <Button 
              onClick={() => setDepenseModalOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="add-depense-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle dépense
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* COLONNE GAUCHE - Recettes */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-200 bg-emerald-50 py-3 px-4">
                  <CardTitle className="font-chivo text-base flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                    <span className="text-emerald-800">Recettes</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-emerald-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Livre chronologique des recettes encaissées</p>
                      </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                </CardHeader>
                
                {/* Filtres Recettes */}
                <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchRecettes}
                        onChange={(e) => setSearchRecettes(e.target.value)}
                        className="pl-8 h-8 text-sm"
                        data-testid="search-recettes"
                      />
                    </div>
                    <Select value={filterModeRecettes} onValueChange={setFilterModeRecettes}>
                      <SelectTrigger className="w-[110px] h-8 text-sm" data-testid="filter-mode-recettes">
                        <Filter className="h-3 w-3 mr-1" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODES_PAIEMENT.map(mode => (
                          <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <CardContent className="p-0">
                  {filteredRecettes.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider py-3">Date</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1">
                                  Réf.
                                  <HelpCircle className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Numéro de la facture ou du justificatif</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nature</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Montant</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Mode</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredRecettes.map((recette, index) => (
                            <TableRow 
                              key={recette.id} 
                              className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-emerald-50/50 transition-colors`}
                            >
                              <TableCell className="font-mono text-sm py-3">{formatDate(recette.date)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{recette.reference}</TableCell>
                              <TableCell className="text-sm font-medium text-slate-800">{recette.client}</TableCell>
                              <TableCell className="text-sm text-slate-600 max-w-[100px] truncate">{recette.nature}</TableCell>
                              <TableCell className="font-mono text-sm text-emerald-600 font-semibold text-right">
                                {formatMontant(recette.montant)}
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{recette.mode_encaissement}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 hover:bg-slate-200"
                                        onClick={() => handleEditRecette(recette)}
                                        data-testid={`edit-recette-${recette.id}`}
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-slate-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Modifier</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 hover:bg-red-100"
                                        onClick={() => setDeleteDialog({ open: true, type: 'recette', id: recette.id })}
                                        data-testid={`delete-recette-${recette.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Supprimer</TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-emerald-100 hover:bg-emerald-100 border-t-2 border-emerald-200">
                            <TableCell colSpan={4} className="font-bold text-emerald-800 py-3">
                              TOTAL RECETTES
                            </TableCell>
                            <TableCell className="font-mono font-bold text-emerald-700 text-right text-lg">
                              {formatMontant(totalRecettes)}
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  ) : recettes.length > 0 ? (
                    <div className="py-12 text-center">
                      <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucun résultat pour cette recherche</p>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucune recette ce mois</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* COLONNE DROITE - Dépenses */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-200 bg-red-50 py-3 px-4">
                  <CardTitle className="font-chivo text-base flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                    <span className="text-red-800">Dépenses</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-red-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Registre des achats et autres dépenses</p>
                      </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                </CardHeader>
                
                {/* Filtres Dépenses */}
                <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchDepenses}
                        onChange={(e) => setSearchDepenses(e.target.value)}
                        className="pl-8 h-8 text-sm"
                        data-testid="search-depenses"
                      />
                    </div>
                    <Select value={filterModeDepenses} onValueChange={setFilterModeDepenses}>
                      <SelectTrigger className="w-[110px] h-8 text-sm" data-testid="filter-mode-depenses">
                        <Filter className="h-3 w-3 mr-1" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODES_PAIEMENT.map(mode => (
                          <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <CardContent className="p-0">
                  {filteredDepenses.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider py-3">Date</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1">
                                  Réf.
                                  <HelpCircle className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Numéro de la facture ou du justificatif</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Fournisseur</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Nature</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Montant</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Mode</TableHead>
                            <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredDepenses.map((depense, index) => (
                            <TableRow 
                              key={depense.id} 
                              className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-red-50/50 transition-colors`}
                            >
                              <TableCell className="font-mono text-sm py-3">{formatDate(depense.date)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{depense.reference}</TableCell>
                              <TableCell className="text-sm font-medium text-slate-800">{depense.fournisseur}</TableCell>
                              <TableCell className="text-sm text-slate-600 max-w-[100px] truncate">{depense.nature}</TableCell>
                              <TableCell className="font-mono text-sm text-red-600 font-semibold text-right">
                                {formatMontant(depense.montant)}
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{depense.mode_paiement}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 hover:bg-slate-200"
                                        onClick={() => handleEditDepense(depense)}
                                        data-testid={`edit-depense-${depense.id}`}
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-slate-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Modifier</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 hover:bg-red-100"
                                        onClick={() => setDeleteDialog({ open: true, type: 'depense', id: depense.id })}
                                        data-testid={`delete-depense-${depense.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Supprimer</TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-red-100 hover:bg-red-100 border-t-2 border-red-200">
                            <TableCell colSpan={4} className="font-bold text-red-800 py-3">
                              TOTAL DÉPENSES
                            </TableCell>
                            <TableCell className="font-mono font-bold text-red-700 text-right text-lg">
                              {formatMontant(totalDepenses)}
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  ) : depenses.length > 0 ? (
                    <div className="py-12 text-center">
                      <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucun résultat pour cette recherche</p>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucune dépense ce mois</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Carte SOLDE DU MOIS */}
            <Card className={`border ${getSoldeBgColor()} shadow-lg max-w-md mx-auto`} data-testid="solde-card">
              <CardContent className="p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Scale className={`h-6 w-6 ${getSoldeColor()}`} />
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1">
                      <span className="text-sm font-medium text-slate-600 uppercase tracking-wider">
                        Solde du mois
                      </span>
                      <HelpCircle className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Différence entre recettes et dépenses du mois</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className={`font-mono text-3xl font-bold tabular-nums ${getSoldeColor()}`}>
                  {formatMontantAvecSigne(solde)}
                </p>
                <div className="mt-4 pt-4 border-t border-slate-200/50 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Recettes</p>
                    <p className="font-mono font-semibold text-emerald-600">{formatMontant(totalRecettes)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Dépenses</p>
                    <p className="font-mono font-semibold text-red-600">{formatMontant(totalDepenses)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Modals */}
        <RecetteModal 
          open={recetteModalOpen} 
          onClose={() => {
            setRecetteModalOpen(false);
            setEditingRecette(null);
          }}
          onSuccess={handleRecetteSuccess}
          editData={editingRecette}
          defaultMonth={moisConfig.num}
          defaultYear={selectedYear}
        />
        
        <DepenseModal 
          open={depenseModalOpen} 
          onClose={() => {
            setDepenseModalOpen(false);
            setEditingDepense(null);
          }}
          onSuccess={handleDepenseSuccess}
          editData={editingDepense}
          defaultMonth={moisConfig.num}
          defaultYear={selectedYear}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-chivo">Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer cette transaction ? Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-btn">Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default MonthPage;
